import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api.js';
import useOfflineQueue from '../../hooks/useOfflineQueue.js';
import { time24, dateTime24 } from '../../utils/time24.js';

const MILESTONES = [
  { key: 'dispatched', label: '1. Dispatched', icon: 'fa-ship' },
  { key: 'arrived', label: '2. Arrived On-site', icon: 'fa-anchor' },
  { key: 'allfast', label: '3. All Fast', icon: 'fa-link' },
  { key: 'commenced', label: '4. Commenced', icon: 'fa-play-circle' },
  { key: 'letgo', label: '5. Let Go', icon: 'fa-undo' },
  { key: 'returning', label: '6. Returning', icon: 'fa-reply' },
  { key: 'secured', label: '7. Back at Station', icon: 'fa-anchor' },
];

const QUEUE_KEY = 'captain_pwa_pending';

// 9031/9032: the Captain Console reads times on the 24-hour clock.
function stamp(ts) {
  return ts ? time24(ts) : '--:--';
}

const fmtWhen = dateTime24;

// Visual treatment per maintenance state surfaced to the boat's crew.
const MAINT_STATE = {
  active: { cls: 'border-warning bg-warning-subtle', icon: 'fa-wrench text-warning', label: 'In maintenance now' },
  upcoming: { cls: 'border-info bg-info-subtle', icon: 'fa-calendar-day text-info', label: 'Scheduled' },
  ready: { cls: 'border-success bg-success-subtle', icon: 'fa-circle-check text-success', label: 'Ready for operation' },
};

// Dispatch notices: a job taken off the captain (replaced, cancelled) or given to them.
const NOTICE_STYLE = {
  withdrawn: { cls: 'alert-warning', icon: 'fa-right-left' },
  cancelled: { cls: 'alert-danger', icon: 'fa-ban' },
  assigned: { cls: 'alert-info', icon: 'fa-bell' },
};

export default function CaptainPwaPage() {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [milestones, setMilestones] = useState({}); // type -> loggedAt
  const [clockRunning, setClockRunning] = useState(false);
  const [clockStart, setClockStart] = useState(null);
  const [clockStopped, setClockStopped] = useState(false);
  const [actionError, setActionError] = useState('');
  const [elapsed, setElapsed] = useState(0);

  const [acknowledged, setAcknowledged] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [remarksSaved, setRemarksSaved] = useState(false);

  const jobIdRef = useRef(null);
  const remarksDirtyRef = useRef(false);

  const send = useCallback(async (item) => {
    if (item.kind === 'ack') return api.acknowledgeJob(item.jobId);
    if (item.kind === 'clock') return api.logClock(item.jobId, item.payload);
    if (item.kind === 'remarks') return api.saveRemarks(item.jobId, item.payload.remarks);
    return api.logMilestone(item.jobId, item.payload);
  }, []);

  // Offline-first queue (NFR-2): clock/milestone actions persist and replay on
  // reconnect. Shared with the Pilot PWA via useOfflineQueue.
  const { syncStatus, enqueueOrSend } = useOfflineQueue(QUEUE_KEY, send);

  // 8955: `jobId` opens one of the captain's jobs; otherwise the current one.
  const load = useCallback(async (jobId) => {
    const t = await api.getActiveTicket(jobId);
    setTicket(t);
    if (t) {
      if (jobIdRef.current && jobIdRef.current !== t.id) {
        // Another job: nothing of the previous job's clock or remarks carries over.
        remarksDirtyRef.current = false;
        setClockRunning(false);
        setClockStart(null);
        setElapsed(0);
        setActionError('');
        setRemarksSaved(false);
      }
      jobIdRef.current = t.id;
      setAcknowledged(t.ack_status === 'Acknowledged');
      // A ticket response that arrives after the captain has started typing
      // (the page can load the ticket more than once) must not wipe the text.
      if (!remarksDirtyRef.current) setRemarks(t.captain_remarks || '');
      const ms = {};
      for (const m of t.milestones || []) ms[m.milestone_type] = m.logged_at;
      setMilestones(ms);
      // Derive clock state from the latest clock event.
      const events = t.clockEvents || [];
      const last = events[events.length - 1];
      if (last && last.action === 'start') {
        setClockRunning(true);
        setClockStart(new Date(last.logged_at).getTime());
      }
      setClockStopped(events.some((e) => e.action === 'stop'));
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load(new URLSearchParams(window.location.search).get('jobId') || undefined);
      } catch (err) {
        setLoadError(err.message || 'Failed to load the dispatch ticket.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  // Notices from dispatch: a job taken off this captain or reassigned to them.
  const [notices, setNotices] = useState([]);
  useEffect(() => {
    const fetchNotices = () =>
      api
        .getCaptainNotices()
        .then((rows) => setNotices(rows || []))
        .catch(() => {});
    fetchNotices();
    const timer = setInterval(fetchNotices, 60_000);
    return () => clearInterval(timer);
  }, []);
  const dismissNotice = async (id) => {
    setNotices((list) => list.filter((n) => n.id !== id));
    try {
      await api.dismissCaptainNotice(id);
    } catch {
      // Shown again on the next refresh if it did not save.
    }
  };

  // Live timer.
  useEffect(() => {
    if (!clockRunning || !clockStart) return undefined;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - clockStart) / 1000)), 1000);
    setElapsed(Math.floor((Date.now() - clockStart) / 1000));
    return () => clearInterval(id);
  }, [clockRunning, clockStart]);

  const switchJob = async (jobId) => {
    setLoading(true);
    setLoadError('');
    try {
      await load(jobId);
    } catch (err) {
      setLoadError(err.message || 'Failed to open that job.');
    } finally {
      setLoading(false);
    }
  };

  // The captain accepts the job before working it; the office sees who and when.
  const acknowledge = async () => {
    setAcknowledged(true);
    await enqueueOrSend({ kind: 'ack', jobId: jobIdRef.current, payload: {} });
  };

  const startClock = async () => {
    const loggedAt = new Date().toISOString();
    setActionError('');
    setClockRunning(true);
    setClockStart(Date.now());
    const res = await enqueueOrSend({ kind: 'clock', jobId: jobIdRef.current, payload: { action: 'start', loggedAt } });
    if (res?.rejected) {
      setClockRunning(false);
      setClockStart(null);
      setElapsed(0);
      setActionError(res.rejected.message);
    }
  };

  const stopClock = async () => {
    const loggedAt = new Date().toISOString();
    setActionError('');
    setClockRunning(false);
    setClockStopped(true);
    const res = await enqueueOrSend({ kind: 'clock', jobId: jobIdRef.current, payload: { action: 'stop', loggedAt } });
    if (res?.rejected) {
      setClockRunning(true);
      setClockStopped(false);
      setActionError(res.rejected.message);
    }
  };

  const logMilestone = async (key) => {
    const loggedAt = new Date().toISOString();
    const previous = milestones[key];
    setActionError('');
    setMilestones((m) => ({ ...m, [key]: loggedAt }));
    const res = await enqueueOrSend({
      kind: 'milestone',
      jobId: jobIdRef.current,
      payload: { milestoneType: key, loggedAt },
    });
    if (res?.rejected) {
      setMilestones((m) => {
        const next = { ...m };
        if (previous) next[key] = previous;
        else delete next[key];
        return next;
      });
      setActionError(res.rejected.message);
    }
  };

  // The job may be worked from shortly before the vessel's scheduled time (8849).
  const opensAt = ticket?.execution_opens_at ? new Date(ticket.execution_opens_at) : null;
  const notYetOpen = Boolean(opensAt && Date.now() < opensAt.getTime());
  // A step is closed once a later one is logged (8853) or the clock has stopped (8851).
  const milestoneLocked = (key) => {
    if (!acknowledged || clockStopped) return true;
    if (key !== 'dispatched' && notYetOpen) return true;
    const idx = MILESTONES.findIndex((m) => m.key === key);
    return MILESTONES.slice(idx + 1).some((m) => milestones[m.key]);
  };

  const saveRemarks = async () => {
    await enqueueOrSend({ kind: 'remarks', jobId: jobIdRef.current, payload: { remarks } });
    setRemarksSaved(true);
  };

  const hhmmss = `${String(Math.floor(elapsed / 3600)).padStart(2, '0')}:${String(
    Math.floor((elapsed % 3600) / 60)
  ).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  const synced = syncStatus === 'Sync OK' || syncStatus === 'Sync Success';

  return (
    <div className="container-fluid py-4" data-testid="captain-pwa-page">
      <div className="bg-primary text-white p-3 rounded shadow d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <i className="fas fa-ship me-2 fa-lg" aria-hidden="true" />
          <div>
            <h6 className="mb-0 fw-bold">Tug PWA Terminal</h6>
            <small className="opacity-75">{ticket?.tug_name || 'Tug'} — Active Shift</small>
          </div>
        </div>
        <span
          className={`badge ${synced ? 'bg-light text-dark' : 'bg-warning text-dark'}`}
          data-testid="signal-status"
        >
          <i className={`fas ${synced ? 'fa-wifi text-success' : 'fa-wifi-slash text-danger'} me-1`} aria-hidden="true" />
          {syncStatus}
        </span>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="pwa-load-error">
          {loadError}
        </div>
      )}

      {notices.map((n) => (
        <div
          key={n.id}
          className={`alert ${NOTICE_STYLE[n.kind]?.cls || 'alert-warning'} d-flex justify-content-between align-items-start`}
          role="alert"
          data-testid="captain-notice"
          data-kind={n.kind}
        >
          <div className="me-3">
            <i className={`fas ${NOTICE_STYLE[n.kind]?.icon || 'fa-bell'} me-2`} aria-hidden="true" />
            <strong>{n.message}</strong>
            <div className="small">
              {fmtWhen(n.scheduledAt)}
              {n.reason ? ` · Reason: ${n.reason}` : ''}
            </div>
          </div>
          <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => dismissNotice(n.id)} data-testid="dismiss-notice">
            Got it
          </button>
        </div>
      ))}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : !ticket ? (
        <div className="alert alert-secondary" data-testid="no-ticket">
          No active dispatch ticket assigned.
        </div>
      ) : (
        <div className="row g-3">
          {/* 8955: the captain's jobs — one booking can give them its docking and, later, its undocking */}
          {(ticket.myJobs || []).length > 1 && (
            <div className="col-12">
              <div className="card border-0 shadow-sm" data-testid="my-jobs">
                <div className="card-body py-2 d-flex flex-wrap gap-2 align-items-center">
                  <label htmlFor="my-job" className="small fw-bold text-muted mb-0">
                    <i className="fas fa-list-ol me-1" aria-hidden="true" />
                    My jobs ({ticket.myJobs.filter((j) => j.status !== 'Completed').length} open)
                  </label>
                  <select
                    id="my-job"
                    className="form-select form-select-sm w-auto flex-grow-1"
                    value={ticket.id}
                    onChange={(e) => switchJob(e.target.value)}
                    data-testid="my-job-select"
                  >
                    {ticket.myJobs.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.jobNo || `Ticket ${j.ticketNo}`} · {j.jobName} · {j.vesselName || '—'} · {fmtWhen(j.scheduledAt)}
                        {j.status === 'Completed' ? ' · Completed' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          {!acknowledged && (
            <div className="col-12">
              <div className="alert alert-warning d-flex flex-wrap justify-content-between align-items-center mb-0" data-testid="ack-required">
                <div className="me-3">
                  <div className="fw-bold">
                    <i className="fas fa-bell me-2" aria-hidden="true" />
                    New job — Ticket No. {ticket.ticket_no ?? '—'}
                  </div>
                  <span className="small">
                    Read the ticket, then acknowledge it so dispatch knows you have the job. The clock and
                    milestones open once you do.
                  </span>
                </div>
                {/* 9028: acknowledging is the go-ahead — green */}
                <button className="btn btn-success fw-bold mt-2 mt-md-0" onClick={acknowledge} data-testid="ack-job">
                  <i className="fas fa-check me-1" aria-hidden="true" />
                  Acknowledge Job
                </button>
              </div>
            </div>
          )}
          <div className="col-lg-4">
          <div className="card border-0 shadow-sm mb-3" data-testid="active-ticket">
            <div className="card-header bg-white pt-3 border-bottom-0 pb-1">
              <h6 className="mb-0 text-muted fw-bold text-uppercase small">
                <i className="fas fa-ticket-alt me-1 text-primary" aria-hidden="true" />
                Active Dispatch Ticket
              </h6>
            </div>
            <div className="card-body pt-2">
              <div className="bg-light p-3 rounded small border">
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Ticket No.</span>
                  <span className="fw-bold" data-testid="ticket-no">{ticket.ticket_no ?? '—'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Booking</span>
                  <span className="fw-bold">{ticket.booking_ref}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Vessel</span>
                  <span className="fw-bold">{ticket.vessel_name || '—'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Planned Time</span>
                  <span className="fw-bold" data-testid="ticket-planned-time">{fmtWhen(ticket.scheduled_at)}</span>
                </div>
                {/* 8955: Booking → Service → Job → Dispatch Ticket */}
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Service</span>
                  <span className="fw-bold" data-testid="ticket-service">{ticket.service_name || ticket.movement_type}</span>
                </div>
                {ticket.job_no && (
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Job</span>
                    <span className="fw-bold text-end" data-testid="ticket-job">
                      {ticket.job_no} — {ticket.job_name}
                      {ticket.booking_job_count > 1 && (
                        <small className="d-block text-muted fw-normal">
                          job {ticket.job_seq} of {ticket.booking_job_count} on this booking
                        </small>
                      )}
                    </span>
                  </div>
                )}
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Trading Status</span>
                  <span className="fw-bold" data-testid="ticket-trading">{ticket.trading_status || '—'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Station / Berth</span>
                  <span className="fw-bold">{ticket.berth_name || ticket.port_name || '—'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Service Mode</span>
                  <span className="fw-bold" data-testid="ticket-service-mode">{ticket.service_mode || 'Normal towage'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Pilot</span>
                  <span className="fw-bold" data-testid="ticket-pilot">
                    {ticket.pilot_name
                      ? `${ticket.pilot_name}${ticket.pilot_selection_type ? ` (${ticket.pilot_selection_type})` : ''}`
                      : ticket.pilot_status || 'None'}
                  </span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Working Channel</span>
                  <span className="fw-bold" data-testid="ticket-channel">{ticket.working_channel || '—'}</span>
                </div>
                {ticket.special_instructions && (
                  <div className="mt-2 pt-2 border-top" data-testid="ticket-instructions">
                    <span className="text-muted d-block">Special Instructions</span>
                    <span className="fw-semibold">{ticket.special_instructions}</span>
                  </div>
                )}
                {acknowledged && (
                  <div className="text-success mt-2 small" data-testid="ticket-acknowledged">
                    <i className="fas fa-check-circle me-1" aria-hidden="true" />
                    Acknowledged
                    {ticket.acknowledged_at ? ` ${fmtWhen(ticket.acknowledged_at)}` : ''}
                    {ticket.acknowledged_by ? ` by ${ticket.acknowledged_by}` : ''}
                  </div>
                )}
                {ticket.issued_by && (
                  <div className="text-muted mt-2 small" data-testid="ticket-issued-by">
                    Issued by {ticket.issued_by}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3" data-testid="captain-remarks">
            <div className="card-header bg-white pt-3 border-bottom-0 pb-1">
              <h6 className="mb-0 text-muted fw-bold text-uppercase small">
                <i className="fas fa-pen-to-square me-1 text-primary" aria-hidden="true" />
                Captain&apos;s Remarks
              </h6>
            </div>
            <div className="card-body pt-2">
              <label htmlFor="captain-remarks-input" className="visually-hidden">Captain&apos;s remarks</label>
              <textarea
                id="captain-remarks-input"
                className="form-control form-control-sm"
                rows={3}
                maxLength={2000}
                value={remarks}
                onChange={(e) => {
                  remarksDirtyRef.current = true;
                  setRemarks(e.target.value);
                  setRemarksSaved(false);
                }}
                disabled={Boolean(ticket.sof_submitted)}
                placeholder="Anything the office should know about this job"
                data-testid="remarks-input"
              />
              {ticket.sof_submitted ? (
                <div className="text-muted small mt-2" data-testid="remarks-locked">
                  Locked — the Statement of Facts is signed.
                </div>
              ) : (
                <div className="d-flex align-items-center mt-2">
                  <button className="btn btn-outline-primary btn-sm" onClick={saveRemarks} data-testid="save-remarks">
                    Save Remarks
                  </button>
                  {remarksSaved && (
                    <span className="text-success small ms-2" data-testid="remarks-saved">
                      <i className="fas fa-check me-1" aria-hidden="true" />
                      Saved
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3" data-testid="boat-maintenance">
            <div className="card-header bg-white pt-3 border-bottom-0 pb-1">
              <h6 className="mb-0 text-muted fw-bold text-uppercase small">
                <i className="fas fa-screwdriver-wrench me-1 text-primary" aria-hidden="true" />
                Boat Maintenance &amp; Readiness
              </h6>
            </div>
            <div className="card-body pt-2">
              {(ticket.maintenance || []).length === 0 ? (
                <div className="text-muted small d-flex align-items-center" data-testid="no-maintenance">
                  <i className="fas fa-circle-check text-success me-2" aria-hidden="true" />
                  No upcoming downtime — boat is cleared for operation.
                </div>
              ) : (
                <div className="d-flex flex-column gap-2" data-testid="maintenance-list">
                  {ticket.maintenance.map((m) => {
                    const s = MAINT_STATE[m.state] || MAINT_STATE.upcoming;
                    return (
                      <div
                        key={m.id}
                        className={`border rounded p-2 small ${s.cls}`}
                        data-testid={`maint-${m.state}`}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold">
                            <i className={`fas ${s.icon} me-1`} aria-hidden="true" />
                            {m.eventType}
                          </span>
                          <span className="badge bg-light text-dark border">{s.label}</span>
                        </div>
                        <div className="text-muted mt-1">
                          {m.state === 'ready' ? (
                            <>Completed {fmtWhen(m.completedAt)} — back in service.</>
                          ) : (
                            <>
                              {fmtWhen(m.startTime)} → {fmtWhen(m.endTime)}
                            </>
                          )}
                        </div>
                        {m.notes && <div className="fst-italic text-muted mt-1">{m.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          </div>

          <div className="col-lg-8">
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-white pt-3 border-bottom-0 pb-1">
              <h6 className="mb-0 text-muted fw-bold text-uppercase small">
                <i className="fas fa-stopwatch me-1 text-danger" aria-hidden="true" />
                Job Clocks &amp; Milestones
              </h6>
            </div>
            <div className="card-body text-center pt-2">
              <div
                className="bg-dark text-warning p-3 rounded fw-bold mb-4 shadow"
                style={{ fontFamily: 'monospace', fontSize: '1.8rem', letterSpacing: 2 }}
                data-testid="exec-timer"
              >
                {hhmmss}
              </div>

              <div className="row mb-3">
                <div className="col-6">
                  <button
                    className="btn btn-outline-success btn-lg w-100"
                    onClick={startClock}
                    disabled={clockRunning || clockStopped || !acknowledged || notYetOpen}
                    data-testid="start-clock"
                  >
                    <i className="fas fa-play me-1" aria-hidden="true" />
                    Start Clock
                  </button>
                </div>
                <div className="col-6">
                  <button
                    className="btn btn-outline-danger btn-lg w-100"
                    onClick={stopClock}
                    disabled={!clockRunning}
                    data-testid="stop-clock"
                  >
                    <i className="fas fa-stop me-1" aria-hidden="true" />
                    Stop Clock
                  </button>
                </div>
              </div>

              {notYetOpen && acknowledged && (
                <div className="alert alert-info py-2 small text-start" data-testid="execution-not-open">
                  <i className="fas fa-clock me-1" aria-hidden="true" />
                  The vessel is scheduled for {fmtWhen(ticket.scheduled_at)}. The clock and on-site milestones
                  open at {fmtWhen(opensAt)}.
                </div>
              )}
              {clockStopped && (
                <div className="alert alert-secondary py-2 small text-start" data-testid="clock-stopped">
                  <i className="fas fa-flag-checkered me-1" aria-hidden="true" />
                  Job clock stopped — the time ladder is closed. Finalize the Statement of Facts.
                </div>
              )}
              {actionError && (
                <div className="alert alert-danger py-2 small text-start" role="alert" data-testid="pwa-action-error">
                  {actionError}
                </div>
              )}

              <hr className="my-3" />
              <h6 className="text-muted fw-bold small text-start text-uppercase mb-3">
                <i className="fas fa-list-ol me-1" aria-hidden="true" />
                Statement of Facts time ladder
              </h6>

              <div className="d-flex flex-column">
                {MILESTONES.map((m) => (
                  <button
                    key={m.key}
                    className={`btn ${milestones[m.key] ? 'btn-success text-white' : 'btn-light text-secondary'} text-start p-3 mb-2`}
                    onClick={() => logMilestone(m.key)}
                    disabled={milestoneLocked(m.key)}
                    data-testid={`milestone-${m.key}`}
                  >
                    <i className={`fas ${m.icon} me-2`} aria-hidden="true" />
                    {m.label}
                    <span className="float-end fw-bold small" data-testid={`milestone-stamp-${m.key}`}>
                      {stamp(milestones[m.key])}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
